import { createAxiosInstance } from '../utils/axios.js'
import { API_BASE_URL } from '../config.js'

const api = createAxiosInstance()

export const userService = {
  // Digital purchases the logged-in customer can read/listen to in-app.
  async getDigitalPurchases () {
    try {
      const response = await api.get('/api/user/digital-purchases')
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to fetch your library'
      }
    }
  },

  // Absolute URLs for vendor-restricted (in-app, non-downloadable) content.
  // Purchase URLs require the session cookie (withCredentials); the sample URL
  // is public. Absolute form is needed so the <audio> element hits the API origin.
  contentUrl (purchaseId) {
    return `${API_BASE_URL}/api/user/digital-purchases/${purchaseId}/content`
  },

  streamUrl (purchaseId) {
    return `${API_BASE_URL}/api/user/digital-purchases/${purchaseId}/stream`
  },

  sampleContentUrl (digitalProductId) {
    return `${API_BASE_URL}/api/digital-products/${digitalProductId}/sample-content`
  },

  // Read/listen resume position (cross-device). position is an opaque per-format
  // cursor: audiobook seconds, EPUB CFI, or PDF scroll offset.
  async getProgress (purchaseId) {
    try {
      const res = await api.get(`/api/user/digital-purchases/${purchaseId}/progress`)
      return res.data?.position ?? null
    } catch (error) {
      return null
    }
  },

  async saveProgress (purchaseId, position) {
    try {
      await api.put(`/api/user/digital-purchases/${purchaseId}/progress`, { position: String(position) })
      return true
    } catch (error) {
      return false
    }
  },

  // Title/author/cover for the reader/player header.
  async getPurchaseMeta (purchaseId) {
    const res = await this.getDigitalPurchases()
    if (!res.success) return null
    const id = Number(purchaseId)
    const item = (res.data || []).find(p => Number(p.id) === id)
    return item
      ? { title: item.title, author: item.author, cover: item.cover_image, fileFormat: item.file_format }
      : null
  },

  async getSampleMeta (digitalProductId) {
    try {
      const res = await api.get(`/api/digital-products/${digitalProductId}/sample-meta`)
      const d = res.data || {}
      return { title: d.title, author: d.author, cover: d.cover_image, fileFormat: d.file_format }
    } catch (error) {
      return null
    }
  },

  async getReceipts (page = 1, limit = 10) {
    try {
      const response = await api.get('/api/user/receipts', {
        params: { page, limit }
      })
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to fetch receipts'
      }
    }
  },

  async getTickets (page = 1, limit = 10) {
    try {
      const response = await api.get('/api/user/tickets', {
        params: { page, limit }
      })
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to fetch tickets'
      }
    }
  },

  async getSales () {
    try {
      const response = await api.get('/api/user/sales')
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to fetch sales'
      }
    }
  },

  async getCurrentUser () {
    try {
      const response = await api.get('/api/user/me')
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error || error.message || 'Failed to fetch user'
      }
    }
  },

  async updateAddresses (shippingAddress, billingAddress) {
    try {
      const response = await api.put('/api/user/addresses', {
        shipping_address: shippingAddress,
        billing_address: billingAddress
      })
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to update addresses'
      }
    }
  },

  async updateName (firstName, lastName, phoneNumber) {
    try {
      const response = await api.put('/api/user/name', {
        first_name: firstName || null,
        last_name: lastName || null,
        phone_number: phoneNumber || null
      })
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to update name'
      }
    }
  },

  async requestEmailChange (newEmail) {
    try {
      const response = await api.put('/api/user/email', {
        email: newEmail
      })
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to request email change'
      }
    }
  },

  async changePassword (currentPassword, newPassword) {
    try {
      const response = await api.put('/api/user/password', {
        current_password: currentPassword,
        new_password: newPassword
      })
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to change password'
      }
    }
  },

  async deleteAccount () {
    try {
      const response = await api.delete('/api/user/account')
      return { success: true, data: response.data }
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          'Failed to delete account'
      }
    }
  }
}
